import React from 'react';
import Table from '../../components/common/Table';
import { MemberCompetencyDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge } from '../../utils/displayUtils';
import FileIcons from '../common/FileIcons';
import { getRoles } from '../../utils/tokenUtils';
import { competencyStatusLabel, getCompetencyExpiryInfo, getCompetencyStatus } from '../../utils/competencyUtils';

interface MemberCompetencyTableProps {
  data: MemberCompetencyDTO[];
  handleEdit: (row: MemberCompetencyDTO) => void;
  handleDelete: (row: MemberCompetencyDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const MemberCompetencyTable: React.FC<MemberCompetencyTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  const roles = getRoles();
  return (
    <Table
      columns={[
        ...(roles?.includes('Admin')
          ? [
            {
              label: 'Member',
              key: 'member',
              render: (row: MemberCompetencyDTO) => (
                <div className="flex flex-col gap-1">
                  <span>{row.member?.firstName} {row.member?.lastName}</span>
                  <span>{row.member?.idPassportNumber ?? ''}</span>
                </div>
              ),
            }]
          : []),
        {
          label: 'Type',
          key: 'competencyTypeId',
          render: (row: MemberCompetencyDTO) => (
            <span>{row.competencyType?.title ?? 'Unknown'}</span>
          ),
        },

        {
          label: 'Number',
          key: 'certificateNumber',
        },

        {
          label: 'Certificate',
          key: 'files',
          render: (row: MemberCompetencyDTO) => (
            <FileIcons
              files={[
                {
                  id: row.competencyCertificateId,
                  label: 'Competency Certificate'
                }
              ]}
            />
          ),
        },
        {
          label: 'Issue Date',
          key: 'competencyIssueDate',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? '', true),
        },

        {
          label: 'Renew by',
          key: 'competencyExpiryDate',
          render: (row: MemberCompetencyDTO) => {
            const info = getCompetencyExpiryInfo(row.competencyExpiryDate);
            return <span className={info.className}>{info.display}</span>; // shows date and days, negative after expiry
          },
        },
        {
          label: 'Status',
          key: 'isExpired',
          render: (row: MemberCompetencyDTO) => {
            const s = getCompetencyStatus(row);  // 'active' | 'expiring' | 'expired'
            const isGood = s === 'active';
            const falseVariant: 'error' | 'warning' = s === 'expired' ? 'error' : 'warning';
            return renderBooleanBadge(isGood, competencyStatusLabel[s], competencyStatusLabel[s], falseVariant);
          },
        },



        {
          label: 'Added',
          key: 'createdUtc',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? '', true),
        },
      ]}
      data={data}
      actions={roles?.includes('User') ? {
        onEdit: handleEdit,
        onDelete: handleDelete,
      } : {}}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
    />
  );
};

export default MemberCompetencyTable;
